import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { WivPromotionCard, useWivTeaserHandler } from '@frontend/self-upgrade';
import { theme } from '@frontend/theme';
import { NakedButton, SpinningLoader } from '@frontend/design-system';
import { insuranceTabTrackingIds } from '../tracking-ids';
import { INSURANCE_STATUS_MAPPING, unverifiedStatus } from '../utils/constants';
import { InsuranceStatusChip } from './insurance-status-chip';

interface Props {
  locationId: string;
  onClick: () => void;
  handleVerifyStatusClick?: () => void;
  isLoading?: boolean;
  shouldShowVerifyButton?: boolean;
  insuranceCoverageStatus: string | undefined;
  hasOverrideCoverage?: boolean;
  hasErrors: boolean;
}

export const InsuranceStatusChipButton = ({
  locationId,
  insuranceCoverageStatus,
  hasErrors,
  onClick,
  handleVerifyStatusClick,
  hasOverrideCoverage,
  isLoading = false,
  shouldShowVerifyButton = true,
}: Props) => {
  const {
    isLoading: isLoadingWivEligibility,
    showWeaveVerify,
    showWeaveVerifyPromotion,
  } = useWivTeaserHandler({ locationIds: [locationId] });

  const normalizedStatus = (insuranceCoverageStatus || '').toLowerCase();
  const statusKey =
    hasOverrideCoverage || (!hasErrors && insuranceCoverageStatus) ? normalizedStatus : hasErrors ? 'error' : null;

  const statusInfo = statusKey ? INSURANCE_STATUS_MAPPING.get(statusKey) : undefined;

  if (isLoading || isLoadingWivEligibility) return <SpinningLoader size='xs' />;

  if (!showWeaveVerify && !showWeaveVerifyPromotion) return null;

  if (!statusInfo && shouldShowVerifyButton) {
    return (
      <div css={showWeaveVerifyPromotion && wivPromotionWrapperStyles}>
        <VerifyInsuranceButton
          showWeaveVerify={showWeaveVerify}
          onClick={handleVerifyStatusClick ? handleVerifyStatusClick : onClick}
        />
        {showWeaveVerifyPromotion && (
          <WivPromotionCard source='appointments' locationIds={[locationId]} cardStyles={wivPromotionCardStyles} />
        )}
      </div>
    );
  }
  if (!statusInfo) {
    return <InsuranceStatusChip statusInfo={unverifiedStatus} />;
  }

  return (
    <NakedButton css={{ width: 'fit-content' }} onClick={onClick}>
      <InsuranceStatusChip statusInfo={statusInfo} />
    </NakedButton>
  );
};

type VerifyInsuranceButtonProps = {
  showWeaveVerify: boolean;
  onClick: () => void;
};

export const VerifyInsuranceButton = ({ showWeaveVerify, onClick }: VerifyInsuranceButtonProps) => {
  const { t } = useTranslation('scheduleCalendarEvents');
  return (
    <NakedButton
      trackingId={insuranceTabTrackingIds.verifyInsuranceButton}
      css={buttonStyle}
      disabled={!showWeaveVerify}
      onClick={onClick}
    >
      <Icon name='shield-small' css={{ color: 'currentcolor' }} />
      {t('Verify')}
    </NakedButton>
  );
};

const buttonStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontWeight: theme.font.weight.bold,
  fontSize: theme.fontSize(14),
  color: theme.colors.primary50,
  [':disabled']: {
    opacity: 0.5,
  },
});

const wivPromotionWrapperStyles = css({
  position: 'relative',
  ['div:last-child']: {
    opacity: 0,
    pointerEvents: 'none',
  },
  [':hover div:last-child']: {
    opacity: 1,
    zIndex: 1,
    pointerEvents: 'auto',
  },
});

const wivPromotionCardStyles = css({
  position: 'absolute',
  left: 0,
  top: '100%',
  width: 'max-content',
  maxWidth: 382,
  boxShadow: theme.shadows.floating,
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.small,
  padding: theme.spacing(2),
  transition: 'opacity 0.25s ease-in-out',
});
